// pages/[[...slug]].tsx
import { AnimatePresence, motion } from "framer-motion";
import type { NextPage, GetServerSidePropsContext } from "next";
import Head from "next/head";
import Image from "next/image";
import { useState, useEffect } from "react"; // SetStateAction removed as VibeType is gone
import { Toaster, toast } from "react-hot-toast";
import Footer from "../../components/Footer";
import LoadingDots from "../../components/LoadingDots";
import ResizablePanel from "../../components/ResizablePanel";
import { log } from 'next-axiom';
import { useCookies } from "react-cookie";
import React from 'react';
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useSession } from "next-auth/react";
import { getServerSession } from "next-auth/next";
import { authOptions } from "../api/auth/[...nextauth]";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Navbar from "../../components/Navbar";
import Tab from "../../components/Tab";
import Features from "../../components/Features";
import Testimonials from "../../components/Testimonials";
import * as CONSTANTS from '../../utils/constants'; // Keep this for CONSTANTS.CATEGORIES
import { CATEGORY_TO_GENERATOR_TYPE } from '../../utils/constants'; // Import the new map

type ServerProps = {
  catInServer: string; // This is derived from slug (e.g. "Image", "Logo") for display purposes
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  let slug = context?.params?.slug;
  let catInServer:string = ""; // For display, e.g., "Image"

  // Root path is now handled by pages/index.tsx, so redirect to home if accessing root
  if (!slug || slug.length === 0) {
    return { redirect: { destination: "/", permanent: false } };
  }

  // For path: /image-generator/prompt-image/
  // slug array will be ["prompt-image"] (not ["image-generator", "prompt-image"])
  if (slug && slug.length > 0) {
    const categorySlug = slug[0]; // e.g., "prompt-image"
    // Validate against the full slug format in CONSTANTS.CATEGORIES
    if (categorySlug && categorySlug.length>0 && CONSTANTS.CATEGORIES.indexOf(categorySlug) > -1) {
      const splitStr = categorySlug.split('-'); // ["prompt", "image"]
      if (splitStr.length > 1) {
        const shortCat = splitStr[1]; // "image"
        // Capitalize for display ("Image")
        catInServer = shortCat.charAt(0).toUpperCase() + shortCat.slice(1);
      } else {
        // Handle cases where categorySlug might be like "image" directly if CATEGORIES changes
         catInServer = categorySlug.charAt(0).toUpperCase() + categorySlug.slice(1);
      }
    } else {
      // If the slug doesn't match any known category slug
      return { notFound: true };
    }
  } else {
    // No slug (e.g., accessing /image-generator/ directly), redirect to home
    return { redirect: { destination: "/", permanent: false } };
  }
  
  return ({
    props: {
        ...(await serverSideTranslations(context?.locale as string, ['common'])),
        catInServer
    }
  });
};

interface GenerationResult {
  prompt: string;
  imageUrl: string;
}

const DynamicPage: NextPage<ServerProps> = ({ catInServer }: ServerProps) => {
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [bio, setBio] = useState("");
  const [generationResult, setGenerationResult] = useState<GenerationResult | null>(null);
  const [cookies,setCookie] = useCookies(["deviceid"]);
  const [category, setCategory] = useState("image"); // Default category state

  const { t, i18n  } = useTranslation("common");
  const { locale } = useRouter();
  const [copied, setCopied] = useState(false);

  const router = useRouter();
  const { data: session, status } = useSession();
  const { slug } = router.query;
  
  const handleCopy = () => {
    setCopied(true);
    toast(t("copied_toast") ||"Copied to clipboard!", {icon: "✂️"});
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };                                  
  
  useEffect(() => {
    if (!cookies["deviceid"]) {
      fetch("/api/genDeviceid")
        .then(response => response.json())
        .then(data => {
          setCookie("deviceid", data.deviceid, {
            path: "/",
            maxAge: 3600*10000,
            sameSite: true,
          });
        })
        .catch(error => console.error("Error fetching deviceId:", error));
    }

    // Set category based on slug
    // For path: /image-generator/prompt-image/
    // slug array will be ["prompt-image"] (not ["image-generator", "prompt-image"])
    if (slug && slug.length > 0) {
      const categorySlug = slug[0]; // e.g., "prompt-image"
      // Ensure categorySlug is in the format "prompt-xxx" and is a valid one
      if (categorySlug && CONSTANTS.CATEGORIES.includes(categorySlug)) {
        const parts = categorySlug.split('-');
        if (parts.length > 1 && parts[0] === "prompt") {
          // Set category to the short form, e.g., "image", "logo"
          setCategory(parts[1]);
        } else {
          // If slug is in CATEGORIES but not "prompt-xxx" (e.g. if CATEGORIES was just ["image", "logo"])
          // This case might not be hit with current CATEGORIES structure but good for robustness
          setCategory(categorySlug);
        }
      } else {
        // Invalid category slug, redirect to home or set a default category
        // router.push('/'); // Option: redirect
        setCategory("image"); // Option: set default if slug invalid
        console.warn(`Invalid category slug: ${categorySlug}, defaulting to 'image'. Consider redirect.`);
      }
    } else {
      // No slug (e.g., accessing /image-generator/ directly), set to default category
      setCategory("image");
    }
  }, [locale, cookies, setCookie, slug, router]); // Added router to dependency for potential push

  const generateBio = async (e: any) => {
    e.preventDefault();

    if (status === "unauthenticated") {
      router.push("/auth/signin");
      return;
    }

    if (!bio || bio.trim() === "") {
      toast.error(t("prompt_required_toast") || "Please enter a prompt.");
      return;
    }

    setLoading(true);
    setGenerationResult(null);

    // Derive numericGeneratorType from category state using the imported map
    // Default to 0 ("image") if category is not in map or undefined.
    const numericGeneratorType = CATEGORY_TO_GENERATOR_TYPE[category as keyof typeof CATEGORY_TO_GENERATOR_TYPE] ?? 0;

    log.info('New generation request', { prompt: bio, generatorType: numericGeneratorType, category: category });

    try {
      const response = await fetch("/api/generateTextImage", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: bio,
          generatorType: numericGeneratorType // Send the numeric type
        }),
      });

      setLoading(false);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "An unexpected error occurred.", reason: "CLIENT_PARSE_ERROR" }));
        console.error("API Error:", errorData);
        if (response.status === 401 || response.status === 403 || errorData.reason === "AUTH_REQUIRED") {
          toast.error(t("login_required_toast") || "Please log in to generate images.");
        } else if (errorData.reason === "LIMIT_REACHED") {
          toast.error(t("limit_reached_toast") || "Daily limit reached. Please try again tomorrow.");
        } else if (errorData.reason === "GLOBAL_LIMIT_REACHED") {
          toast.error(t("global_limit_reached_toast") || "The daily total generation limit for the service has been reached. Please try again tomorrow.");
        } else if (errorData.reason === "INSUFFICIENT_CREDITS") {
          toast.error("You have run out of credits. Redirecting to pricing page.");
          setTimeout(() => {
            router.push('/price');
          }, 3000);
        } else {
          toast.error(errorData.error || `Error: ${response.status} ${response.statusText}`);
        }
        return;
      }

      const resultData = await response.json();

      if (resultData.generatedPrompt && resultData.imageUrl) {
        setGenerationResult({ prompt: resultData.generatedPrompt, imageUrl: resultData.imageUrl });
        toast.success(t("generation_successful_toast") || "Generation successful!");
      } else {
        console.error("API response missing generatedPrompt or imageUrl:", resultData);
        toast.error(t("generation_failed_toast") || "Failed to get valid result from server.");
      }

    } catch (error) {
      setLoading(false);
      console.error("Fetch Error:", error);
      toast.error(t("generation_fetch_error_toast") || "An error occurred while sending the request.");
    }
  };

  const handleEditImage = async () => {
    if (!generationResult?.imageUrl) {
      toast.error("No image to edit.");
      return;
    }

    setIsUploading(true);
    try {
      const response = await fetch('/api/uploadToR2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageUrl: generationResult.imageUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image.');
      }

      const { url: r2Url } = await response.json();
      
      router.push(`/nanobanana-image-editor?imageUrl=${encodeURIComponent(r2Url)}`);

    } catch (error: any) {
      console.error("Failed to upload image for editing:", error);
      toast.error(error.message || "Failed to prepare image for editing.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex max-w-5xl mx-auto flex-col items-center justify-center py-2 min-h-screen">
      <div className="w-full flex justify-end"><Navbar/></div>

      <Head>
        {/* catInServer is used for display (e.g. "Image") */}
        <title>{catInServer ? t("product_name").replace(/Prompt/gi, catInServer) : t("product_name")}</title>
        {!catInServer ? (<link rel="canonical" href="https://www.aigenprompt.com/"></link>) : ""}
        <meta
            name="description"
            content={(catInServer ? t("description").replace(/image/gi, catInServer):t("description")) as string }
          />
        <meta name="keywords" content={(catInServer ? t("keywords").replace(/Prompt/g, catInServer):t("keywords")) as string } />
        <meta name="author" content="Kristin Roberts"/>
        <meta name="robots" content="index, follow"/>
        <meta property="og:site_name" content="AIImageStudio" />
        <meta
            property="og:description"
            content={(catInServer ? t("description").replace(/image/gi, catInServer):t("description")) as string }
          />
        <meta property="og:title" content={(catInServer ? t("product_name").replace(/Prompt/gi, catInServer):t("product_name")) as string } />
        <meta property="og:image" content="https://www.aigenprompt.com/icon.png" />
     </Head>
     <br/><br/><br/>
     {status === "authenticated" && <Tab />}
      <main className="flex flex-1 w-full flex-col items-center justify-center text-center px-4 mt-2 sm:mt-4" >
        {/* Show promotional text and generation interface only when user is logged in */}
        {status === "authenticated" && (
          <>
            {/* === MOVED AND RESTYLED PROMOTIONAL TEXT HERE === */}
            <div className="w-full text-center mt-8 mb-24">
              <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-800 font-medium">
                <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {t('promo_text_part1')}
                <strong>{t('promo_text_part2_strong')}</strong>
                {t('promo_text_part3')}
                <strong>{t('promo_text_part4_strong')}</strong>
                {t('promo_text_part5')}
              </div>
            </div>
            {/* === END OF PROMOTIONAL TEXT === */}

            <h1 className="sm:text-6xl text-4xl max-w-2xl font-bold text-slate-900 ">
            { catInServer?t("intro").replace(/image/gi, (catInServer.charAt(0).toLowerCase() + catInServer.slice(1))):t("intro")}
            </h1>
            <div className="max-w-xl w-full">
              <div className="flex mt-10 items-center space-x-3">
                <p className="text-left font-medium"> {t("writesubject")}{" "} </p>
              </div>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black my-5"
                placeholder={ t("placeholder") as string }
              />

              {!loading && (
                <button
                  className="bg-black rounded-xl text-white font-medium px-4 py-2 sm:mt-10 mt-8 hover:bg-black/80 w-full"
                  onClick={(e) => generateBio(e)}
                >
                  {t("btn_generate")}
                </button>
              )}
              {loading && (
                <button
                  className="bg-black rounded-xl text-white font-medium px-4 py-2 sm:mt-10 mt-8 hover:bg-black/80 w-full"
                  disabled
                >
                  <LoadingDots color="white" style="large" />
                </button>
              )}
            </div>
            <Toaster
              position="top-center"
              reverseOrder={false}
              toastOptions={{ duration: 3000 }}
            />
            <hr className="h-px bg-gray-700 border-1 dark:bg-gray-700" />
            <ResizablePanel>
              <AnimatePresence mode="wait">
                <motion.div className="space-y-10 my-10">
                  {generationResult && !loading && (
                    <>
                      <div>
                        <h2 className="sm:text-4xl text-3xl font-bold text-slate-900 mx-auto">
                          {t("yourResultHeading") || "Your Generated Result"}
                        </h2>
                      </div>
                      <div className="space-y-8 flex flex-col items-center justify-center max-w-xl mx-auto">
                        {generationResult.imageUrl && (
                          <div className="w-full p-2 border rounded-lg shadow-md bg-gray-50 flex flex-col items-center">
                             <Image
                                src={generationResult.imageUrl}
                                alt={t("generatedImageAltText") || "Generated image from prompt"}
                                width={512}
                                height={512}
                                className="rounded-lg object-contain"
                                unoptimized={true}
                              />
                            <div className="flex items-center justify-center space-x-4 mt-4">
                              <a
                                href={`${generationResult.imageUrl}?download`}
                                download={`aiimageStudio-${generationResult.prompt.substring(0,20)}.png`}
                                className="bg-white border border-black rounded-xl text-black font-medium px-4 py-2 hover:bg-gray-100"
                              >
                                {t('Download')}
                              </a>
                              <button
                                onClick={handleEditImage}
                                disabled={isUploading}
                                className="bg-white border border-black rounded-xl text-black font-medium px-4 py-2 hover:bg-gray-100 disabled:bg-gray-200"
                              >
                                {isUploading ? <LoadingDots color="white" style="small" /> : "Edit Image"}
                              </button>
                            </div>
                          </div>
                        )}
                        {generationResult.prompt && (
                          <div className="w-full">
                            <h3 className="text-xl text-slate-800 mb-2">
                              {t("generatedPromptHeading") || "Generated Prompt"}
                            </h3>
                            <CopyToClipboard text={generationResult.prompt} onCopy={handleCopy}>
                              <div
                                className="bg-white rounded-xl shadow-md p-4 hover:bg-gray-100 transition cursor-copy border text-left"
                              >
                                <p>{generationResult.prompt}</p>
                              </div>
                            </CopyToClipboard>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </ResizablePanel>
          </>
        )}
      </main>
      
      {/* Show Features and Testimonials only when user is not logged in */}
      {status === "unauthenticated" && (
        <>
          <Features />
          <Testimonials />
        </>
      )}
      
      <Footer />
    </div>
  );
};

export default DynamicPage;
